import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Box, Text } from '@rsos/components/capstone/base';
import { AppPathsConsumer } from '../../../AppPathsContext';

/**
 * SmartLink renders a sidebar link either as a client-side react-router NavLink
 * (when the target is one of the current app's top-level paths) or as a plain
 * anchor (for links into other apps' bundles, which require a full navigation).
 * As more admin pages are consolidated into a single app, more sidebar links
 * resolve as internal and navigate without a full page reload.
 */
const SmartLink = ({ to, children, toolTip, name, ...rest }) => {
  const inner = (
    <>
      {children}
      {toolTip ? (
        <TooltipChild>
          <InlineText size="small" fontWeight="bold">
            {toolTip.primary}
          </InlineText>
          <InlineText size="small">&nbsp;{toolTip.secondary}</InlineText>
        </TooltipChild>
      ) : null}
    </>
  );

  return (
    <AppPathsConsumer>
      {appPaths => {
        // Internal when the target is one of the current app's owned paths or a
        // sub-route of one (sidebar links are often sub-paths, e.g.
        // `/admin/data-source/roles` under the owned `/admin/data-source`).
        const isInternal =
          Array.isArray(appPaths) &&
          appPaths.some(path => to === path || to.startsWith(`${path}/`));
        return isInternal ? (
          <StyledNavLink to={to} data-name={name} {...rest}>
            {inner}
          </StyledNavLink>
        ) : (
          <StyledAnchor href={to} data-name={name} {...rest}>
            {inner}
          </StyledAnchor>
        );
      }}
    </AppPathsConsumer>
  );
};

export default SmartLink;

export const TooltipChild = styled(Box).attrs(() => ({
  position: 'absolute',
  color: 'background',
  bg: 'primaryText',
  borderRadius: '5px',
  p: '10px',
}))`
  display: none;
  width: 197px;
  left: 100%;
  z-index: 1;
  top: -50%;
`;
export const InlineText = styled(Text).attrs()`
  display: inline;
`;

const linkStyles = css`
  text-decoration: none;
  padding: 0px 20px;
  position: relative;
  &:hover {
    background: ${p => p.theme.colors.heavyLineOutline};
    ${TooltipChild} {
      display: inline;
    }
  }
  &.active {
    background: ${p => p.theme.colors.primaryBaseC2};
  }
`;

const StyledAnchor = styled.a`
  ${linkStyles}
`;

const StyledNavLink = styled(NavLink)`
  ${linkStyles}
`;
